import React, { useRef, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";

/**
 * SmartDateInput — single-field date input that accepts both Quicken-
 * style shortcuts AND ISO date strings, with the native date picker
 * tucked behind a small calendar icon for click selection.
 *
 * Shortcut tokens (case-insensitive, committed on Enter / Tab / Blur):
 *
 *   T  / TODAY      → today
 *   Y  / YESTERDAY  → yesterday
 *   TM / TOMORROW   → tomorrow                (also ``+1``)
 *   +N / -N         → N days from / before today
 *   NW / +NW / -NW  → N weeks      (``2W`` = a fortnight)
 *   NM / +NM / -NM  → N months     (``6M`` = the standard MOT-due
 *                                   pattern; handles Jan-31 + 1M → 28/29 Feb)
 *
 * Anything that already parses as ISO (``YYYY-MM-DD``) passes through.
 * Unrecognised input is silently rolled back to the last good value.
 *
 *   Props
 *     value      — ISO date string (``YYYY-MM-DD``) or ""
 *     onChange   — fires with an event-shaped ``{target: {value: iso}}``
 *                  so it's a drop-in for ``<input type="date">``
 *     hint       — when true, renders a small "T = today" helper line
 *     dataTestid — testid root; the visible input becomes that, the
 *                  picker icon becomes ``${dataTestid}-picker``
 */

const MS_PER_DAY = 86_400_000;

function pad(n) {
  return String(n).padStart(2, "0");
}

function toISODate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function addMonths(date, months) {
  const d = new Date(date.getTime());
  const targetMonth = d.getMonth() + months;
  d.setMonth(targetMonth);
  if (d.getMonth() !== ((targetMonth % 12) + 12) % 12) {
    d.setDate(0);
  }
  return d;
}

/** Resolve a shortcut string to an ISO date, or null if unrecognised. */
export function resolveDateShortcut(raw, today = new Date()) {
  if (!raw) return null;
  const s = String(raw).trim().toUpperCase();
  if (!s) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  if (s === "T"  || s === "TODAY")     return toISODate(base);
  if (s === "Y"  || s === "YESTERDAY") {
    return toISODate(new Date(base.getTime() - MS_PER_DAY));
  }
  if (s === "TM" || s === "TOMORROW" || s === "+1") {
    return toISODate(new Date(base.getTime() + MS_PER_DAY));
  }

  const dayMatch = s.match(/^([+\-])(\d+)$/);
  if (dayMatch) {
    const sign = dayMatch[1] === "+" ? 1 : -1;
    const n = parseInt(dayMatch[2], 10);
    return toISODate(new Date(base.getTime() + sign * n * MS_PER_DAY));
  }

  const weekMatch = s.match(/^([+\-]?)(\d+)W$/);
  if (weekMatch) {
    const sign = weekMatch[1] === "-" ? -1 : 1;
    const n = parseInt(weekMatch[2], 10);
    return toISODate(new Date(base.getTime() + sign * n * 7 * MS_PER_DAY));
  }

  const monthMatch = s.match(/^([+\-]?)(\d+)M$/);
  if (monthMatch) {
    const sign = monthMatch[1] === "-" ? -1 : 1;
    const n = parseInt(monthMatch[2], 10);
    return toISODate(addMonths(base, sign * n));
  }

  return null;
}

/** Human-readable display of an ISO date — what the field SHOWS when
 *  not being edited. e.g. "2026-03-21" → "21 Mar 2026". */
function formatDisplay(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-").map((p) => parseInt(p, 10));
  const monthName = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m - 1];
  return `${pad(d)} ${monthName} ${y}`;
}

export default function SmartDateInput({
  value = "",
  onChange = () => {},
  hint = false,
  className = "",
  dataTestid,
  disabled = false,
  ...rest
}) {
  // While the user has focus, the buffer drives the display so each
  // keystroke is preserved. When NOT editing, the display falls back
  // to the parent-provided value directly — no useEffect / setState
  // mirroring needed (which keeps the React hooks linter happy and
  // avoids a render storm if the parent re-renders on every keystroke).
  const [editing, setEditing] = useState(false);
  const [buffer, setBuffer] = useState("");
  const pickerRef = useRef(null);

  const commit = (raw) => {
    const candidate = raw === undefined ? buffer : raw;
    if (!candidate || !candidate.trim()) {
      if (value) onChange({ target: { value: "" } });
      setBuffer("");
      return;
    }
    const iso = resolveDateShortcut(candidate);
    if (iso) {
      if (iso !== value) onChange({ target: { value: iso } });
      setBuffer(iso);
    } else {
      // Unrecognised — roll back silently to the last known good value.
      setBuffer(value || "");
    }
  };

  const onFocus = () => {
    setEditing(true);
    // When the field gets focus, show the raw ISO so typing replaces it
    // cleanly. The user can then type T / Y / +7 etc.
    setBuffer(value || "");
  };

  const onBlur = () => {
    commit();
    setEditing(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
      e.currentTarget.blur();
    }
  };

  const onPickerChange = (e) => {
    const iso = e.target.value || "";
    onChange({ target: { value: iso } });
    setBuffer(iso);
  };

  const display = editing
    ? buffer
    : value
    ? formatDisplay(value)
    : buffer;

  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <input
          type="text"
          value={display}
          onChange={(e) => setBuffer(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder="Type T / Y / +7 / 6M or pick →"
          disabled={disabled}
          className={`w-full bg-white border border-slate-200 rounded-md pl-3 pr-9 py-2 text-[15px] text-slate-900 focus:outline-none focus:border-teal-500 disabled:bg-slate-50 disabled:text-slate-500 ${className}`}
          data-testid={dataTestid || "smart-date-input"}
          autoComplete="off"
          spellCheck="false"
          {...rest}
        />
        {/* Tiny calendar icon — clicks open the native picker for the
            FOH who wants to click rather than type. The native input is
            visually hidden but functionally identical. */}
        <button
          type="button"
          onClick={() => pickerRef.current?.showPicker?.() || pickerRef.current?.click?.()}
          className="absolute inset-y-0 right-1 my-auto h-7 w-7 flex items-center justify-center rounded text-slate-500 hover:text-teal-700 hover:bg-teal-50 disabled:opacity-50"
          disabled={disabled}
          aria-label="Open date picker"
          data-testid={dataTestid ? `${dataTestid}-open-picker` : "smart-date-open-picker"}
        >
          <CalendarIcon className="w-4 h-4" />
        </button>
        <input
          ref={pickerRef}
          type="date"
          value={value || ""}
          onChange={onPickerChange}
          disabled={disabled}
          className="sr-only absolute right-0 bottom-0 w-0 h-0 opacity-0 pointer-events-none"
          tabIndex={-1}
          data-testid={dataTestid ? `${dataTestid}-picker` : "smart-date-picker"}
        />
      </div>
      {hint && (
        <p className="text-[10px] text-slate-500">
          Tip: type <kbd className="px-1 bg-slate-100 border rounded">T</kbd>= today,{" "}
          <kbd className="px-1 bg-slate-100 border rounded">Y</kbd>= yesterday,{" "}
          <kbd className="px-1 bg-slate-100 border rounded">+7</kbd>= a week away,{" "}
          <kbd className="px-1 bg-slate-100 border rounded">6M</kbd>= 6 months — then Enter or Tab.
        </p>
      )}
    </div>
  );
}
